<template>
  <div class="bot-ui bot-ui--animate">
    <transition name="fadeUp">
      <div
        class="board"
        :class="{
          fullscreen: isFullscreen,
        }"
        v-show="botActive"
        ref="board"
      >
        <OliverBotHeader
          @toggle-fullscreen="onToggleFullscreen"
          @close-chat="closeChat"
        />
        <vue-advanced-chat
          ref="vac"
          height="100%"
          :current-user-id="String(loggedInUser.id)"
          :room-id="selectedChat && selectedChat.roomId"
          :rooms="JSON.stringify(castedChats)"
          :loading-rooms="initiateChatsLoading"
          :rooms-loaded="chatsLoaded"
          :messages="JSON.stringify(castedMessages)"
          :text-messages="JSON.stringify(textMessages)"
          :messages-loaded="messagesLoaded"
          load-first-room="false"
          show-add-room="true"
          show-audio="false"
          user-tags-enabled="false"
          show-reaction-emojis="false"
          :responsive-breakpoint="3000"
          room-info-enabled="false"
          show-files="true"
          show-new-messages-divider="false"
          custom-search-room-enabled="true"
          allow-copy="true"
          :accepted-files="castedAcceptedFiles"
          :textarea-action-enabled="shouldDisplayTextareaAction"
          :styles="JSON.stringify(customChatStyle)"
          :disable-sending="messageGenerating"
          :username-options="
            JSON.stringify({ minUsers: 0, currentUser: false })
          "
          @add-room="createChatHelper"
          @fetch-more-rooms="getChatsHelper(chats.page + 1)"
          @fetch-messages="getMessagesHelper($event.detail[0])"
          @send-message="sendMessageHelper($event.detail[0])"
          @toggle-rooms-list="handleToggleChatList($event.detail[0])"
          @search-room="applySearch"
          @open-file="openFile($event.detail[0])"
        >
          <div slot="custom-action-icon">
            <i
              v-if="cancelChatRunLoading"
              class="fas fa-circle-notch text-danger"
              style="font-size: 20px; margin: 0px 7px"
            ></i>
            <i
              v-else
              class="fa fa-stop-circle text-danger cursor-pointer"
              style="font-size: 20px; margin: 0px 7px"
              @click="
                cancelChatRunHelper(selectedChat.roomId, currentChatRunId)
              "
            ></i>
          </div>
          <div slot="send-icon" v-if="messageGenerating || sendMessageLoading">
            <i
              class="fas fa-spin fa-circle-notch"
              style="font-size: 20px; margin: 0px 7px"
            ></i>
          </div>
        </vue-advanced-chat>
        <div
          v-if="failedMessage && !messageGenerating"
          class="oliver-retry-banner"
          role="alert"
        >
          <i
            class="fas fa-exclamation-circle text-danger"
            aria-hidden="true"
          ></i>
          <span class="oliver-retry-banner__text">
            {{ failedMessage.error_message }}
          </span>
          <button
            v-if="failedMessage.retryable !== false"
            type="button"
            class="oliver-retry-banner__btn"
            :disabled="retryInFlight"
            @click="retryFailedMessageHelper"
          >
            <i
              v-if="retryInFlight"
              class="fas fa-spin fa-circle-notch"
              aria-hidden="true"
            ></i>
            <i v-else class="fas fa-redo" aria-hidden="true"></i>
            {{ retryInFlight ? "Retrying..." : "Retry" }}
          </button>
        </div>
      </div>
    </transition>
    <div class="bot-bubble">
      <button class="bubble-btn" @click="botToggle">
        <div class="bot-bubble-img" ref="botBubbleImg">
          <img :src="bubbleImg" alt="bot-logo" />
        </div>
      </button>
    </div>
  </div>
</template>
<script>
import { register } from "@ray-solutions/vue-advanced-chat";
import dayjs from "dayjs";
import { debounce } from "lodash";
import Swal from "sweetalert2";
import { mapActions, mapState } from "vuex";
import OliverBotHeader from "./Header.vue";
import { getFileExtension } from "@/utils/fileHelpers.js";

const MIME_EXTENSIONS = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/json": "json",
  "application/zip": "zip",
  "text/csv": "csv",
  "text/plain": "txt",
  "text/markdown": "md",
  "text/html": "html",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
};
register();

export default {
  components: {
    OliverBotHeader,
  },
  data() {
    return {
      botActive: true,
      isFullscreen: false,
      initiateChatsLoading: false,
      textMessages: {
        ROOMS_EMPTY: "Click (+) icon to create a new chat",
        SEARCH: "Search for a chat",
        MESSAGES_EMPTY:
          "Hello! my name is Oliver your clinic assistant, how can I help?",
        CONVERSATION_STARTED: "",
      },
      customChatStyle: {
        general: {
          colorSpinner: "#195ea2",
        },
        content: {
          background: "#f0f4f8",
        },
        header: {
          background: "#ffffff",
        },
        message: {
          colorStarted: "#2d3748",
          colorUsername: "#195ea2",
        },
      },
      acceptedFiles: [
        "text/x-c",
        "text/x-c++",
        ".csv",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/html",
        "text/x-java",
        "application/json",
        "text/markdown",
        "application/pdf",
        "text/x-php",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "text/x-python",
        "text/x-script.python",
        "text/x-ruby",
        "text/x-tex",
        "text/plain",
        "text/css",
        "image/jpeg",
        "image/jpeg",
        "text/javascript",
        "image/gif",
        "image/png",
        "application/x-tar",
        "application/typescript",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/xml",
        "application/zip",
      ],
      limit: 30,
      filters: {
        title: "",
      },
      retryInFlight: false,
    };
  },
  mounted() {
    this.displayDisclaimerDialog();
    this.getChatsHelper();
    this.keepMediaPreviewClearOfSidebar();
  },
  destroyed() {
    if (this.selectedChat && this.currentChatRunId) {
      this.cancelChatRunHelper(this.selectedChat.roomId, this.currentChatRunId);
    }
  },
  computed: {
    ...mapState({
      companyData: (state) => state.settings.companyProfile,
      chatsLoading: (state) => state.OliverChatBot.chatHistory.loading,
      chats: (state) => state.OliverChatBot.chatHistory.pagination,
      chatMessagesLoading: (state) => state.OliverChatBot.chatMessages.loading,
      chatMessages: (state) => state.OliverChatBot.chatMessages.pagination,
      selectedChat: (state) => state.OliverChatBot.selectedChat,
      loggedInUser: (state) => state.auth.user,
      messageGenerating: (state) => state.OliverChatBot.messageGenerating,
      cancelChatRunLoading: (state) => state.OliverChatBot.cancelChatRunLoading,
      currentChatRunId: (state) => state.OliverChatBot.currentChatRunId,
      sendMessageLoading: (state) => state.OliverChatBot.sendMessageLoading,
    }),
    bubbleImg: function () {
      return (
        this.companyData.bot_logo ||
        this.companyData.logo ||
        require("@/assets/img/icons/open-ai-default.svg")
      );
    },
    chatsLoaded: function () {
      return this.chats.page == this.chats.lastPage;
    },
    messagesLoaded: function () {
      return !this.chatMessagesLoading && !this.chatMessages.nextCursor;
    },
    castedChats: function () {
      return this.chats.data.map((chat) => {
        return {
          roomId: String(chat.id),
          roomName: chat.title || "New Chat",
          title: chat.title,
          threadId: chat?.thread_id || chat?.conversation_id,
          index: chat.created_at,
          lastMessage: null,
          users: [
            {
              _id: String(this.loggedInUser.id),
              username: this.loggedInUser.name,
              avatar: this.loggedInUser.profile_picture,
            },
            {
              _id: String(-1),
              username: "Oliver AI Assistant",
              avatar: this.bubbleImg,
            },
          ],
        };
      });
    },
    castedMessages: function () {
      return this.chatMessages.data.map((msg) => {
        return {
          _id: String(msg.id),
          indexId: msg.id,
          content: msg.content,
          senderId: String(msg.participant ? msg.participant.id : -1),
          system: msg.system ? true : false,
          username: msg.participant
            ? msg.participant.name
            : "Oliver AI Assistant",
          avatar: msg.participant ? msg.participant.avatar : this.bubbleImg,
          disableActions: true,
          disableReactions: true,
          files: msg.attachments.map((attachment) => {
            const attach = {
              name: attachment.name,
              size: attachment.size,
              extension: this.attachmentExtension(attachment),
              type: attachment.type == "image_file" ? "png" : attachment.type,
              audio: false,
              url: attachment.url,
            };
            if ("progress" in attachment) {
              attach.progress = attachment.progress;
            }
            return attach;
          }),
        };
      });
    },
    shouldDisplayTextareaAction: function () {
      return Boolean(this.messageGenerating && this.currentChatRunId);
    },
    castedAcceptedFiles: function () {
      return this.acceptedFiles.join(", ");
    },
    failedMessage: function () {
      const messages = this.chatMessages.data || [];
      for (let i = messages.length - 1; i >= 0; i--) {
        const m = messages[i];
        if (m.role === "user" && m.status === "failed") {
          return m;
        }
      }
      return null;
    },
  },
  methods: {
    ...mapActions({
      getChats: "OliverChatBot/getChats",
      createChat: "OliverChatBot/createChat",
      getChatMessages: "OliverChatBot/getChatMessages",
      setSelectedChat: "OliverChatBot/setSelectedChat",
      sendChatMessage: "OliverChatBot/sendChatMessage",
      storeChatMessage: "OliverChatBot/storeChatMessage",
      generateChatTitle: "OliverChatBot/generateChatTitle",
      resetChatMessages: "OliverChatBot/resetChatMessages",
      establishSSEConnection: "OliverChatBot/establishSSEConnection",
      cancelChatRun: "OliverChatBot/cancelChatRun",
      uploadFile: "OliverChatBot/uploadFile",
      retryFailedMessage: "OliverChatBot/retryFailedMessage",
    }),
    /**
     * Derive the bare extension vue-advanced-chat renders on an attachment
     * card. The API reports `type` as a MIME type, which is not what this field
     * expects.
     *
     * `type` is checked before the file name on purpose: on a locally echoed
     * message it already holds a bare extension, and the library has stripped
     * the extension off `name` on that copy, so reading the name first would
     * turn "Q1.2026 Report.docx" into "2026 report".
     */
    attachmentExtension: function (attachment) {
      const type = attachment.type || "";

      if (type == "image_file") {
        return "png";
      }

      if (type && !type.includes("/") && type != "file" && type != "image") {
        return type;
      }

      if (MIME_EXTENSIONS[type]) {
        return MIME_EXTENSIONS[type];
      }

      return getFileExtension(attachment.name).replace(".", "") || type;
    },
    /**
     * Keep the full-screen image preview clear of the application sidebar.
     *
     * Opening an image attachment covers the viewport, but the chat sits below
     * the sidebar in the stacking order, so the sidebar is painted over the
     * left of the preview. Starting the preview at the sidebar edge matches how
     * the widget already stops short of it in fullscreen, and tracks the
     * sidebar collapsing because the width is a custom property.
     *
     * The preview lives in the chat component's shadow root, which application
     * styles cannot reach, so the rule has to be injected there.
     */
    keepMediaPreviewClearOfSidebar: function () {
      customElements.whenDefined("vue-advanced-chat").then(() => {
        const root = this.$refs.vac && this.$refs.vac.shadowRoot;
        if (!root || root.getElementById("oliver-media-preview-fit")) {
          return;
        }
        const style = document.createElement("style");
        style.id = "oliver-media-preview-fit";
        style.textContent =
          ".vac-media-preview{left:var(--app-sidebar-width, 0px);width:calc(100vw - var(--app-sidebar-width, 0px))}";
        root.appendChild(style);
      });
    },
    retryFailedMessageHelper: function () {
      if (!this.selectedChat || this.retryInFlight) {
        return;
      }
      this.retryInFlight = true;
      this.retryFailedMessage(this.selectedChat.roomId).finally(() => {
        this.retryInFlight = false;
      });
    },
    getChatsHelper: function (page = 1) {
      if (page == 1) {
        this.initiateChatsLoading = true;
      }
      return this.getChats({ page, limit: this.limit, ...this.filters }).then(
        (res) => {
          if (res) {
            this.initiateChatsLoading = false;
          }
          return res;
        }
      );
    },
    createChatHelper: function () {
      this.createChat().then((res) => {
        if (res) {
          this.getChatsHelper().then((res) => {
            if (res) {
              const firstChat = res.data?.data?.[0] || null;
              if (firstChat) {
                const formattedChat = this.formatChat(firstChat);
                this.setSelectedChat(formattedChat);
              }
            }
          });
        }
      });
    },
    getMessagesHelper: function ({ room, options }) {
      if (this.chatMessagesLoading) {
        return;
      }
      if (options?.reset) {
        this.setSelectedChat(room);
      }
      this.getChatMessages({
        chatId: room.roomId,
        cursor: options?.reset ? null : this.chatMessages.nextCursor,
        limit: this.limit,
      });
    },
    sendMessageHelper: async function (details) {
      const vm = this;
      const messageContent = details.content;
      const chatId = details.roomId;
      const files = details.files || [];
      const currentChat = this.chats.data.find((chat) => {
        return chat.id == chatId;
      });
      const formattedMessage = this.formatMessage(messageContent, files);
      const message = await this.storeChatMessage(formattedMessage);
      const attachments = await this.uploadFilesHelper(files, message);
      const failed = attachments.some((attachment) => attachment == false);
      if (!failed) {
        const attachmentsToSend = attachments.map(
          (attachment) => attachment.file_id
        );
        vm.sendChatMessage({
          chatId: chatId,
          data: {
            message: messageContent,
            file_ids: attachmentsToSend.length > 0 ? attachmentsToSend : null,
          },
        }).then((res) => {
          if (res) {
            if (!currentChat?.title) {
              vm.generateChatTitle({ chatId, message: messageContent });
            }
            vm.establishSSEConnection(chatId);
          }
        });
      }
    },
    uploadFilesHelper: function (files, message) {
      const vm = this;
      const promises = [];
      files.forEach((file, index) => {
        promises.push(
          vm.uploadFile({
            file,
            message,
            attachmentId: index,
          })
        );
      });
      return new Promise(function (resolve) {
        Promise.all(promises).then((res) => {
          return resolve(res);
        });
      });
    },
    cancelChatRunHelper: function (chatId, runId) {
      return this.cancelChatRun({
        chatId,
        data: {
          run_id: runId,
        },
      });
    },
    formatChat: function (chat) {
      return {
        roomId: String(chat.id),
        roomName: chat.title || "New Chat",
        threadId: chat?.thread_id || chat?.conversation_id,
        index: chat.created_at,
        lastMessage: null,
        users: [
          {
            _id: String(this.loggedInUser.id),
            username: this.loggedInUser.name,
            avatar: this.loggedInUser.profile_picture,
          },
          {
            _id: String(-1),
            username: "Oliver AI Assistant",
            avatar: this.bubbleImg,
          },
        ],
      };
    },
    formatMessage: function (content, files) {
      return {
        id: this.chatMessages.data.length + 1,
        thread_id:
          this.selectedChat?.thread_id || this.selectedChat?.conversation_id,
        content: content,
        role: "user",
        attachments: files.map((file, index) => {
          return {
            id: index,
            url: file.localUrl,
            name: file.name,
            size: file.size,
            type: file.extension,
            progress: 0,
          };
        }),
        participant: {
          id: this.loggedInUser.id,
          name: this.loggedInUser.name,
          avatar: this.loggedInUser.profile_picture,
        },
        created_at: dayjs().unix(),
      };
    },
    applySearch: debounce(function ({ detail }) {
      const searchText = detail[0].value;
      this.filters.title = searchText;
      if (
        String(searchText).trim().length > 0 ||
        String(searchText).length == 0
      ) {
        this.getChatsHelper();
      }
    }, 500),
    handleToggleChatList: function () {
      if (this.selectedChat && this.currentChatRunId) {
        this.cancelChatRunHelper(
          this.selectedChat.roomId,
          this.currentChatRunId
        );
      }
      this.resetChatMessages();
      this.setSelectedChat(null);
    },
    openFile: function ({ file }) {
      const url = file?.file?.url;
      if (!url) return;
      const fileName = file?.file?.name || "download";

      if (url && url.startsWith("data:")) {
        const [header, base64Data] = url.split(",");
        const mimeMatch = header.match(/data:([^;]+)/);
        const mimeType = mimeMatch ? mimeMatch[1] : "application/octet-stream";

        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    botToggle: function () {
      this.botActive = !this.botActive;
      this.$emit("bot-toggle");
    },
    onToggleFullscreen: function () {
      this.isFullscreen = !this.isFullscreen;
      this.$emit("toggle-fullscreen");
    },
    closeChat: function () {
      this.botActive = false;
      this.$emit("close-chat");
    },
    displayDisclaimerDialog: function () {
      const isDisclaimerDialogShown = window.localStorage.getItem(
        "openAI-has-seen-onBoarding"
      );
      if (isDisclaimerDialogShown) {
        return;
      }
      Swal.fire({
        icon: "",
        title: "Oliver AI",
        width: "46em",
        customClass: {
          title: "mt-4",
        },
        html: ` <div class="justify-text">
        <h2 class='text-center text-danger'>IMPORTANT NOTICE REGARDING  ${this.$clinicLabel(
          { type: "singular", capitalize: true }
        )} assistant 'OLIVER' USAGE</h2>
        <hr>
        <div>While we are thrilled to see you interacting with Oliver, our innovative AI-powered ${this.$clinicLabel(
          { type: "singular", capitalize: false }
        )} assistant, it's vital that we clarify the parameters and limitations of its usage, especially concerning health-related topics.<br><br>Please be advised that Oliver is not a medical professional, nor is it equipped with capabilities to offer medical or health advice. It is a language model developed using AI, and does not have access to personalized medical databases, medical training, or the ability to interpret individual health conditions or situations. Therefore it’s use should primarily be related to administrative tasks and duties related to your profession.<br><br>As such, under no circumstances should Oliver be used for obtaining medical advice, sharing personal information, diagnosing health conditions, or deciding on treatments or health interventions. This includes questions pertaining to medications, symptoms, health conditions, medical procedures, or any other health-related topic.
<br><br>All health-related questions, concerns, or issues should be directed to the appropriate healthcare professional – such as a physician, nurse, pharmacist, or other licensed healthcare ${this.$providerLabel(
          { type: "singular", capitalize: false }
        )} – who has access to your health history, can interpret your specific symptoms or conditions, and can provide personalized advice.<br><br>For healthcare ${this.$providerLabel(
          { type: "plural", capitalize: false }
        )}, Oliver can serve as an excellent support tool for non-clinical tasks. This includes but not limited to; assisting with writing letters, developing clinical reports based on assessment findings, creating routines, aiding in program development, crafting presentations, generating policy and procedure, and providing language translation. However, it should not be used as a tool for diagnosing, treating, or making decisions about health conditions.<br><br>Thank you for your understanding and cooperation in using Oliver responsibly. Your health and safety are of paramount importance to us. If you have any questions or concerns about these guidelines, please reach out to us for further clarification</div>
      </div>`,
        allowOutsideClick: false,
        allowEscapeKey: false,
        confirmButtonText: "I understand",
      }).then(() => {
        localStorage.setItem("openAI-has-seen-onBoarding", true);
      });
    },
  },
};
</script>
<style lang="scss" scoped>
@import "./animation";
.bot-ui {
  display: flex;
  flex-direction: column;
  position: fixed;
  right: 86px;
  left: 0px;
  bottom: 60px;
  z-index: 1000;

  .board {
    display: flex;
    flex-direction: column;
    position: absolute;
    overflow: hidden;
    width: 376px;
    margin-bottom: 10px;
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 3px 30px 0 rgba(0, 0, 0, 0.15);
    right: -66px;
    bottom: 56px;
    height: calc(100vh - 110px - 116px - 8px);
    min-height: 240px;
    transition: all 0.3s ease-in-out;
    @media (max-width: 600px) {
      width: calc(95% + 66px);
    }
    vue-advanced-chat {
      flex: 1 1 auto;
      min-height: 0;
      display: block;
    }
    // Fullscreen: stop at the sidebar edge instead of going under it
    &.fullscreen {
      width: calc(100vw - var(--app-sidebar-width) - 36px);
      height: calc(100vh - 110px - 116px - 8px);
      // Very large screens need a slightly bigger buffer to avoid content clipping
      @media (min-width: 1600px) {
        width: calc(100vw - var(--app-sidebar-width) - 41px);
      }
      // On mobile the sidebar becomes an overlay so it takes no layout space
      @media (max-width: 991.98px) {
        width: calc(100vw - 20px);
      }
    }
  }
  .oliver-retry-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background-color: #fff5f5;
    border-top: 1px solid #fed7d7;
    font-size: 13px;
    color: #742a2a;
    .oliver-retry-banner__text {
      flex: 1;
      line-height: 1.3;
      word-break: break-word;
    }
    .oliver-retry-banner__btn {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 10px;
      border: 1px solid #c53030;
      border-radius: 4px;
      background-color: #fff;
      color: #c53030;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.15s ease;
      &:hover:not(:disabled) {
        background-color: #c53030;
        color: #fff;
      }
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
  .bot-bubble {
    align-self: flex-end;
    padding: 0;
    .bubble-btn {
      position: relative;
      display: block;
      padding: 0;
      outline: 0;
      border: 0;
      overflow: hidden;
      box-shadow: none;
      border-radius: 50%;
      cursor: pointer;
      box-shadow: 0 3px 5px 0 rgba(0, 0, 0, 0.15);
      transition: transform ease-in-out 0.2s;
      background-color: #222222;
      width: 56px;
      height: 56px;
      .bot-bubble-img {
        width: 100%;
        height: 100%;

        img {
          object-fit: cover;
          width: 100%;
          height: 100%;
        }
      }
      &:hover {
        transform: scale(1.1);
      }
    }
  }
}
</style>
